/**
 * Shared calculation utilities for Real Estate Feasibility Calculator
 * This ensures consistent calculations across P&L Sheet and Comparison Tab
 */

/**
 * SINGLE SOURCE OF TRUTH for FSI Breakdown.
 *
 * Computes the full FSI breakdown from formData alone — handles:
 *  - Narrow road (<9m) — only basic FSI + 30% incentive on existing built-up
 *  - 6m road special case (basic + 0.3 incentive if building >30 years)
 *  - MHADA (with titbit area + 26.20 sqm/member incentive FSI)
 *  - TOD (25% TDR / 75% Paid FSI split)
 *  - Non-TOD standard (Paid FSI 0.5 max, rest as TDR)
 *  - "Use Lower FSI" override → proportional reduction of Paid FSI + TDR
 *  - Green FSI (silver / gold / platinum)
 *
 * Used by CalculatorPage (live state), FSICalculator UI, P&L, Comparison.
 * Returns null if plotArea or roadWidth missing.
 */
export const computeFSIBreakdownFromFormData = (fd) => {
  if (!fd) return null;
  const plotAreaInput = parseFloat(fd.plotAreaPerCard) || 0;
  const roadWidthInput = parseFloat(fd.roadWidth) || 0;
  if (plotAreaInput <= 0 || roadWidthInput <= 0) return null;

  const grossPlotArea = fd.plotAreaUnit === "sqft" ? plotAreaInput * 0.092903 : plotAreaInput;
  const roadWidthMeters = fd.roadWidthUnit === "feet" ? roadWidthInput * 0.3048 : roadWidthInput;
  const oldRoadWidening = parseFloat(fd.oldRoadWideningArea) || 0;
  const newRoadWidening = parseFloat(fd.newRoadWideningArea) || 0;
  const totalRoadWidening = oldRoadWidening + newRoadWidening;

  const isMhada = fd.projectClassification === "mhada";
  const isTod = fd.projectClassification === "tod";
  const isCbd = fd.projectClassification === "cbd";
  const titbitArea = (isMhada && fd.hasTitbitArea === "yes") ? (parseFloat(fd.titbitArea) || 0) : 0;
  const netPlotArea = grossPlotArea - newRoadWidening + titbitArea;

  const currentYear = new Date().getFullYear();
  const completionYear = parseInt(fd.completionYear) || currentYear;
  const isOver30Years = (currentYear - completionYear) >= 30;

  const isNarrowRoad = roadWidthMeters > 0 && roadWidthMeters < 9;
  const is6MeterRoad = roadWidthMeters >= 6 && roadWidthMeters < 9;

  const basicFSI = 1.1;
  const basicFSIArea = basicFSI * netPlotArea;

  // Green FSI helper
  let greenFSIPct = 0;
  if (fd.greenRatingRequired === "true") {
    if (fd.greenFSICategory === "silver") greenFSIPct = 0.03;
    else if (fd.greenFSICategory === "gold") greenFSIPct = 0.05;
    else if (fd.greenFSICategory === "platinum") greenFSIPct = 0.07;
  }
  const greenFSI = basicFSI * greenFSIPct;
  const greenFSIArea = greenFSI * netPlotArea;

  // Ancillary helper
  const computeAncillary = (totalBuildable) => {
    let ancR = 0, ancC = 0;
    if (fd.projectType === "residential") ancR = totalBuildable * 0.60;
    else if (fd.projectType === "commercial") ancC = totalBuildable * 0.80;
    else if (fd.projectType === "mixed") {
      ancR = totalBuildable * 0.60 * ((parseFloat(fd.residentialPercentage) || 0) / 100);
      ancC = totalBuildable * 0.80 * ((parseFloat(fd.commercialPercentage) || 0) / 100);
    }
    return { ancR, ancC };
  };

  const tdrReductionFromRoadWidening = (oldRoadWidening * 1) + (newRoadWidening * 2);

  // ===== 6m road (between 6 and 9m) special case =====
  if (is6MeterRoad && !isMhada && !isTod) {
    const incentiveFSI = isOver30Years ? 0.3 : 0;
    const permissibleFSI = basicFSI + incentiveFSI;
    const incentiveFSIArea = incentiveFSI * netPlotArea;
    const totalBuildableArea = basicFSIArea + incentiveFSIArea + greenFSIArea;
    const { ancR, ancC } = computeAncillary(totalBuildableArea);
    return {
      permissibleFSI, basicFSI, paidFSI: 0, totalTDR: 0, amenityTDR: 0, slumTDR: 0,
      greenFSI, incentiveFSI, incentiveFSIArea, is6MeterRoad: true, isOver30Years,
      isMhada: false, isNarrowRoad: false, mhadaFSI: 0, mhadaFSIArea: 0,
      mhadaIncentiveFSI: 0, mhadaIncentiveFSIArea: 0, titbitArea: 0,
      netPlotArea, grossPlotArea, oldRoadWidening, newRoadWidening, totalRoadWidening,
      tdrReductionFromRoadWidening, basicFSIArea, paidFSIArea: 0, tdrArea: 0, greenFSIArea,
      totalBuildableArea, ancillaryResidential: ancR, ancillaryCommercial: ancC,
      totalAncillary: ancR + ancC, totalFSIWithAncillary: totalBuildableArea + ancR + ancC,
      useLowerFSI: false, effectiveFSI: permissibleFSI, reductionRatio: 1,
    };
  }

  // ===== Narrow road (<9m & not 6m, e.g. 0–6m) — non-MHADA only =====
  if (isNarrowRoad && !isMhada && !is6MeterRoad) {
    const existingBuiltupSqm = ((parseFloat(fd.areaFlats) || 0) + (parseFloat(fd.areaOffices) || 0) + (parseFloat(fd.areaShops) || 0)) * 0.092903;
    const incentiveFSI = 0.3;
    const incentiveFSIArea = existingBuiltupSqm * incentiveFSI;
    const totalBuildableArea = basicFSIArea + incentiveFSIArea + greenFSIArea;
    const { ancR, ancC } = computeAncillary(totalBuildableArea);
    return {
      permissibleFSI: basicFSI, basicFSI, paidFSI: 0, totalTDR: 0, amenityTDR: 0, slumTDR: 0,
      greenFSI, incentiveFSI, incentiveFSIArea, is6MeterRoad: false, isOver30Years,
      isMhada: false, isNarrowRoad: true, mhadaFSI: 0, mhadaFSIArea: 0,
      mhadaIncentiveFSI: 0, mhadaIncentiveFSIArea: 0, titbitArea: 0,
      netPlotArea, grossPlotArea, oldRoadWidening, newRoadWidening, totalRoadWidening,
      tdrReductionFromRoadWidening: 0, basicFSIArea, paidFSIArea: 0, tdrArea: 0, greenFSIArea,
      totalBuildableArea, ancillaryResidential: ancR, ancillaryCommercial: ancC,
      totalAncillary: ancR + ancC, totalFSIWithAncillary: totalBuildableArea + ancR + ancC,
      useLowerFSI: false, effectiveFSI: basicFSI, reductionRatio: 0,
    };
  }

  // ===== Standard FSI calc (road >= 9m or MHADA) =====
  let permissibleFSI;
  let cbdBaselineFSI = 0; // Non-TOD "basic entitlement" used only in CBD to size the premium delta
  if (isMhada) permissibleFSI = 3.0;
  else if (isCbd) {
    // CBD (Central Business District) — compulsorily commercial. Higher FSI by road width.
    if (roadWidthMeters >= 27) permissibleFSI = 5.0;
    else if (roadWidthMeters >= 18) permissibleFSI = 4.0;
    else if (roadWidthMeters >= 12) permissibleFSI = 3.0;
    else permissibleFSI = 2.0; // fallback for narrow roads

    // Baseline (non-TOD) FSI serves as the "no-premium" entitlement.
    // Only the delta (permissible − baseline) is Paid Premium FSI @ 50% RR.
    if (roadWidthMeters >= 30) cbdBaselineFSI = 3.0;
    else if (roadWidthMeters >= 24) cbdBaselineFSI = 2.75;
    else if (roadWidthMeters > 12 && roadWidthMeters < 24) cbdBaselineFSI = 2.5;
    else if (roadWidthMeters === 12) cbdBaselineFSI = 2.25;
    else if (roadWidthMeters >= 9) cbdBaselineFSI = 2.0;
    else cbdBaselineFSI = 2.0;
  }
  else if (isTod) {
    if (roadWidthMeters >= 24) permissibleFSI = 4.0;
    else if (roadWidthMeters >= 15) permissibleFSI = 3.5;
    else if (roadWidthMeters >= 12) permissibleFSI = 3.0;
    else if (roadWidthMeters >= 9) permissibleFSI = 2.5;
    else permissibleFSI = 2.0;
  } else {
    if (roadWidthMeters >= 30) permissibleFSI = 3.0;
    else if (roadWidthMeters >= 24) permissibleFSI = 2.75;
    else if (roadWidthMeters > 12 && roadWidthMeters < 24) permissibleFSI = 2.5;
    else if (roadWidthMeters === 12) permissibleFSI = 2.25;
    else if (roadWidthMeters >= 9) permissibleFSI = 2.0;
    else permissibleFSI = 2.0;
  }

  // "Use Lower FSI" override
  const useLower = fd.useLowerFSI === "yes";
  const customLower = parseFloat(fd.customLowerFSI) || 0;
  let effectiveFSI = permissibleFSI;
  if (useLower && customLower > basicFSI && customLower < permissibleFSI) effectiveFSI = customLower;
  const useLowerFSIFlag = effectiveFSI < permissibleFSI;

  const maxRemainingFSI = permissibleFSI - basicFSI;
  const remainingFSI = effectiveFSI - basicFSI;
  const reductionRatio = maxRemainingFSI > 0 ? remainingFSI / maxRemainingFSI : 0;

  // Paid FSI / TDR split
  let totalTDR, paidFSI;
  if (isMhada) { paidFSI = 0; totalTDR = 0; }
  else if (isCbd) {
    // CBD: Paid FSI + TDR follow the SAME non-TOD split — but sized to the non-TOD baseline
    // (cbdBaselineFSI), not the full permissible CBD FSI. The extra FSI above the baseline
    // is a separate "CBD Premium" layer (see cbdPremiumFSI below).
    const localMaxRemaining = Math.max(0, cbdBaselineFSI - basicFSI);
    const effectiveNonPremium = Math.min(effectiveFSI, cbdBaselineFSI);
    const localRemaining = Math.max(0, effectiveNonPremium - basicFSI);
    const localRatio = localMaxRemaining > 0 ? localRemaining / localMaxRemaining : 0;
    paidFSI = 0.5 * localRatio;
    totalTDR = Math.max(0, (localMaxRemaining - 0.5)) * localRatio;
  }
  else if (isTod) {
    totalTDR = maxRemainingFSI * 0.25 * reductionRatio;
    paidFSI = maxRemainingFSI * 0.75 * reductionRatio;
  } else {
    paidFSI = 0.5 * reductionRatio;
    totalTDR = (maxRemainingFSI - 0.5) * reductionRatio;
  }

  const paidFSIArea = paidFSI * grossPlotArea;
  const tdrArea = totalTDR * grossPlotArea;

  // CBD Premium FSI — additional FSI beyond the non-TOD baseline, chargeable at 50% RR
  const cbdPremiumFSI = isCbd ? Math.max(0, permissibleFSI - cbdBaselineFSI) : 0;
  const cbdPremiumArea = cbdPremiumFSI * grossPlotArea;

  // TDR split — prefer tdrSplitRatio (e.g. "50:50") when present, else fall back to slumTDRPercentage
  let slumTDRPct;
  if (fd.tdrSplitRatio && typeof fd.tdrSplitRatio === "string" && fd.tdrSplitRatio.includes(":")) {
    const parts = fd.tdrSplitRatio.split(":").map(Number);
    const slumPart = parts[1];
    if (!isNaN(slumPart) && slumPart >= 0 && slumPart <= 100) {
      slumTDRPct = slumPart / 100;
    }
  }
  if (slumTDRPct === undefined) {
    slumTDRPct = (parseFloat(fd.slumTDRPercentage) || 30) / 100;
  }
  const slumTDR = totalTDR * slumTDRPct;
  const amenityTDR = totalTDR * (1 - slumTDRPct);

  // MHADA FSI + incentive per member
  const mhadaFSI = isMhada ? (effectiveFSI - basicFSI) : 0;
  const mhadaFSIArea = mhadaFSI * netPlotArea;
  const mhadaIncentiveFSI = isMhada ? 26.20 : 0;
  const numberOfMembers = parseInt(fd.numberOfMembers) || 0;
  const mhadaIncentiveFSIArea = isMhada ? (mhadaIncentiveFSI * numberOfMembers) : 0;

  const totalBuildableArea = isMhada
    ? (basicFSIArea + mhadaFSIArea + mhadaIncentiveFSIArea + greenFSIArea)
    : (basicFSIArea + paidFSIArea + tdrArea + greenFSIArea + cbdPremiumArea);

  const { ancR, ancC } = computeAncillary(totalBuildableArea);
  const totalAncillary = ancR + ancC;
  const totalFSIWithAncillary = totalBuildableArea + totalAncillary;

  return {
    permissibleFSI, basicFSI, paidFSI, totalTDR, amenityTDR, slumTDR,
    greenFSI, incentiveFSI: 0, incentiveFSIArea: 0,
    is6MeterRoad: false, isOver30Years, isMhada, isCbd, cbdBaselineFSI, cbdPremiumFSI, cbdPremiumArea, isNarrowRoad: false,
    mhadaFSI, mhadaFSIArea, mhadaIncentiveFSI, mhadaIncentiveFSIArea, titbitArea,
    netPlotArea, grossPlotArea, oldRoadWidening, newRoadWidening, totalRoadWidening,
    tdrReductionFromRoadWidening,
    basicFSIArea, paidFSIArea, tdrArea, greenFSIArea,
    totalBuildableArea, ancillaryResidential: ancR, ancillaryCommercial: ancC,
    totalAncillary, totalFSIWithAncillary,
    useLowerFSI: useLowerFSIFlag, effectiveFSI, reductionRatio,
  };
};

/**
 * Compute complete project metrics from formData alone — no external state needed.
 * Used for project-to-project comparison where we only have the saved formData.
 * Computes: FSI breakdown, FSI costs, DA charges, society expenses, full P&L.
 */
export const computeStandaloneProjectMetrics = (fd, appConfig = null) => {
  if (!fd) return null;
  const fsi = computeFSIBreakdownFromFormData(fd);
  if (!fsi) return null;

  // ===== FSI COSTS =====
  const rrRatePlot = parseFloat(fd.rrRatePlot) || 0;
  const rrRateConstruction = parseFloat(fd.rrRateConstruction) || 0;
  const isMhada = fd.projectClassification === "mhada";
  const commercialPct = parseFloat(fd.commercialPercentage) || 0;
  const isCommercial = fd.projectType === "commercial";
  // Use admin-configurable rates for legacy scrutiny / RADA-RODA (defaults preserved)
  const cfg = (appConfig && appConfig.fsi_costs) || {};
  const SCRUTINY_PER_SQM = cfg.scrutiny_per_sqm ?? 5;
  const RADA_RODA_PER_SQM = cfg.rada_roda_per_sqm ?? 20;
  let fsiTotalCosts = 0;

  if (isMhada) {
    fsiTotalCosts = (fsi.mhadaFSIArea || 0) * rrRatePlot * 0.35;
    // Add ancillary, land dev, building dev etc for MHADA
    fsiTotalCosts += (fsi.totalAncillary || 0) * rrRatePlot * 0.15;
    fsiTotalCosts += fsi.grossPlotArea * rrRatePlot * 0.01;
    fsiTotalCosts += fsi.totalFSIWithAncillary * rrRatePlot * 0.04;
    fsiTotalCosts += fsi.totalFSIWithAncillary * rrRateConstruction * 0.01;
  } else if (!fsi.isNarrowRoad) {
    const amenityTDRPct = parseFloat(fd.amenityTDRPercent) || 0;
    const amenityTDRArea = (fsi.amenityTDR || 0) * fsi.grossPlotArea;
    const slumTDRArea = (fsi.slumTDR || 0) * fsi.grossPlotArea;
    const amenityTDRCost = amenityTDRArea * rrRatePlot * (amenityTDRPct / 100);
    let slumTDRCost = 0;
    if (fd.slumTDRMethod === "zone_drc") {
      slumTDRCost = slumTDRArea * (parseFloat(fd.slumTDRZoneDRC) || 0) * 10.764;
    } else {
      slumTDRCost = slumTDRArea * (parseFloat(fd.slumTDRRRPercent) || 0) * 10.764;
    }
    const ancillaryCost = (fsi.totalAncillary || 0) * rrRatePlot * 0.15;
    const paidFSICost = (fsi.paidFSIArea || 0) * rrRatePlot * 0.35;
    // CBD Premium FSI cost — 50% of Land RR rate on the additional CBD FSI
    const cbdPremiumCost = (fsi.cbdPremiumArea || 0) * rrRatePlot * 0.50;
    const landDevRate = isCommercial ? 0.02 : (0.01 + (commercialPct / 100) * 0.01);
    const landDevCharges = fsi.grossPlotArea * rrRatePlot * landDevRate;
    const buildingDevRate = isCommercial ? 0.08 : (0.04 + (commercialPct / 100) * 0.04);
    const buildingDevCharges = fsi.totalFSIWithAncillary * rrRatePlot * buildingDevRate;
    const labourCessRate = isCommercial ? 0.015 : (0.01 + (commercialPct / 100) * 0.005);
    const upkar = fsi.totalFSIWithAncillary * rrRateConstruction * labourCessRate;
    const fireFighting = fsi.totalFSIWithAncillary * (parseFloat(fd.fireFightingRate) || 0);
    const scrutiny = fsi.totalFSIWithAncillary * SCRUTINY_PER_SQM;
    const radaRoda = fsi.totalFSIWithAncillary * RADA_RODA_PER_SQM;
    const numMembers = parseInt(fd.numMembers) || 0;
    const fourW = parseFloat(fd.fourWheelerPerMember) || 1;
    const twoW = parseFloat(fd.twoWheelerPerMember) || 1;
    const nonFSIArea = (numMembers * fourW * 20) + (numMembers * twoW * 4);
    const nonFSICharges = nonFSIArea * 0.01 * rrRateConstruction;
    fsiTotalCosts = amenityTDRCost + slumTDRCost + ancillaryCost + paidFSICost + cbdPremiumCost + landDevCharges + buildingDevCharges + upkar + fireFighting + scrutiny + radaRoda + nonFSICharges;
  } else {
    // Narrow road: only ancillary
    const ancillaryCost = (fsi.totalAncillary || 0) * rrRatePlot * 0.15;
    const landDevCharges = fsi.grossPlotArea * rrRatePlot * 0.01;
    const buildingDevCharges = fsi.totalFSIWithAncillary * rrRatePlot * 0.04;
    const upkar = fsi.totalFSIWithAncillary * rrRateConstruction * 0.01;
    fsiTotalCosts = ancillaryCost + landDevCharges + buildingDevCharges + upkar;
  }

  // ===== DA CHARGES =====
  const convFactor = 0.092903;
  const existingCarpetSqm = ((parseFloat(fd.areaFlats) || 0) + (parseFloat(fd.areaOffices) || 0) + (parseFloat(fd.areaShops) || 0)) * convFactor;
  const freeAreaPct = parseFloat(fd.freeAreaPercentFlats) || parseFloat(fd.additionalFreeAreaPercent) || 0;
  const additionalFree = existingCarpetSqm * (freeAreaPct / 100);
  const totalCarpetToSociety = existingCarpetSqm + additionalFree;
  const baseValue = totalCarpetToSociety * 1.1 * rrRateConstruction;
  const devCharges = totalCarpetToSociety * 1.1 * 0.02 * rrRatePlot;
  const option1 = baseValue + devCharges;
  const stampDutyRate = parseFloat(fd.stampDutyRate) || 5;
  const stampDuty = Math.max(option1, 0) * (stampDutyRate / 100);
  const totalDACharges = stampDuty + 30000;

  // ===== SOCIETY EXPENSES =====
  const numMembers = parseInt(fd.numMembers) || 0;
  const durationMonths = parseInt(fd.projectDuration) || 36;
  const rentPerSqft = parseFloat(fd.rentPerSqftFlat) || 0;
  const totalRent = existingCarpetSqm / convFactor * rentPerSqft * durationMonths;
  const shiftingCharges = (parseFloat(fd.shiftingCharges) || 0) * numMembers * 2;
  const corpusCharges = (parseFloat(fd.corpusCharges) || 0) * numMembers;
  const bettermentCharges = (parseFloat(fd.bettermentCharges) || 0) * numMembers;
  const parkingPayout = fd.parkingCompensationType === "payout" ? (parseFloat(fd.compensationPayout) || 0) * (parseInt(fd.existingParkings) || 0) : 0;
  const totalSocietyExpenses = totalRent + shiftingCharges + corpusCharges + bettermentCharges + parkingPayout;

  // Now compute the full metrics using calculateOurOfferMetrics with derived costs
  const fsiCostsObj = { totalCosts: fsiTotalCosts };
  const daObj = { totalDACharges };
  const socObj = { totalSocietyExpenses, pmcCost: 0, bgCost: 0, totalGST: 0 };
  const result = calculateOurOfferMetrics(fd, fsi, fsiCostsObj, daObj, socObj, appConfig);
  return result;
};


export const calculateOurOfferMetrics = (formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, appConfig = null) => {
  if (!fsiBreakdown) {
    return null;
  }

  const conversionFactor = 0.092903;

  // ===== Admin-configurable C2 zone rates (Feb 2026) =====
  // Note: All FSI cost line items (TDR scrutiny, lift, demolition, C2 conversion, etc.)
  // are computed in FSISanctionCosts.js and arrive via fsiCosts.totalCosts.
  // Only C2 ADDITIONAL CONSTRUCTION lives here because it adds to construction cost, not FSI costs.
  const cfg = (appConfig && appConfig.fsi_costs) || {};
  const C2_ADDL_CONSTRUCTION_PLOT_PCT = (cfg.c2_additional_construction_plot_percent ?? 50) / 100;

  const _isC2 = (formData.projectType === "commercial" || formData.projectType === "mixed") && formData.isC2Zone === "yes";
  const c2AddlAreaSqm = _isC2 ? ((fsiBreakdown.grossPlotArea || 0) * C2_ADDL_CONSTRUCTION_PLOT_PCT) : 0;
  const c2AddlAreaSqft = c2AddlAreaSqm * 10.7639;
  const c2AddlRate = parseFloat(formData.c2AdditionalConstructionRate) || 0;
  const c2AddlConstructionCost = c2AddlAreaSqft * c2AddlRate;

  // Area calculations
  const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
  const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
  const totalCarpetArea = totalFSIArea * (carpetFactor / 100);

  // Existing area (sft to sq mtr)
  const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
  const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
  const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
  const existingCarpetArea = existingFlats + existingOffices + existingShops;

  // Additional free area - SEPARATE percentages for each property type
  const freeAreaPercentFlats = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const freeAreaPercentOffices = parseFloat(formData.freeAreaPercentOffices) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const freeAreaPercentShops = parseFloat(formData.freeAreaPercentShops) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  
  const additionalFreeCarpetFlats = existingFlats * (freeAreaPercentFlats / 100);
  const additionalFreeCarpetOffices = existingOffices * (freeAreaPercentOffices / 100);
  const additionalFreeCarpetShops = existingShops * (freeAreaPercentShops / 100);
  const additionalFreeCarpet = additionalFreeCarpetFlats + additionalFreeCarpetOffices + additionalFreeCarpetShops;

  // Terrace/Garden converted area (SEPARATE ratios)
  const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
  const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
  const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
  const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
  const convertedTerrace = areaTerrace * (terraceConversionRatio / 100);
  const convertedGarden = areaGarden * (gardenConversionRatio / 100);
  const convertedTerraceGarden = convertedTerrace + convertedGarden;

  // Increment on converted terrace/garden (apply additional free % if toggle is yes)
  const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
  const additionalFreePercentForIncrement = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const incrementOnTerrace = applyIncrementOnTG ? convertedTerrace * (additionalFreePercentForIncrement / 100) : 0;
  const incrementOnGarden = applyIncrementOnTG ? convertedGarden * (additionalFreePercentForIncrement / 100) : 0;
  const totalTerraceGardenWithIncrement = convertedTerraceGarden + incrementOnTerrace + incrementOnGarden;

  // Parking compensation - carpet area (convert sft to sq mtr)
  const parkingCompensationCarpet = formData.parkingCompensationType === "carpet" 
    ? (parseFloat(formData.compensationCarpetArea) || 0) * conversionFactor 
    : 0;

  // Total free area = additional % free + converted terrace/garden (with increment if applicable) + parking
  const totalFreeArea = additionalFreeCarpet + totalTerraceGardenWithIncrement + parkingCompensationCarpet;

  // Balance carpet area for sale
  const balanceCarpetArea = Math.max(0, totalCarpetArea - existingCarpetArea - totalFreeArea);

  // Free area given to society members (in sft, convert to sq mtr)
  const freeAreaToSocietySqft = parseFloat(formData.freeAreaToSociety) || 0;
  const freeAreaToSocietySqMtr = freeAreaToSocietySqft * conversionFactor;

  // Loading percentages for each property type
  const loadingPctFlats = parseFloat(formData.loadingPercentage) || 40;
  const loadingPctOffices = parseFloat(formData.loadingPercentageOffices) || 45;
  const loadingPctShops = parseFloat(formData.loadingPercentageShops) || 50;
  
  // Net carpet area for sale (after deducting free area to society)
  const netCarpetForSale = Math.max(0, balanceCarpetArea - freeAreaToSocietySqMtr);
  const netCarpetForSaleSqft = netCarpetForSale / conversionFactor;

  // Revenue from sales - SEPARATE SALE RATES for each property type
  const saleRateFlats = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;
  const saleRateOffices = parseFloat(formData.saleRateOffices) || saleRateFlats;
  const saleRateShops = parseFloat(formData.saleRateShops) || saleRateFlats;
  
  // For backward compatibility
  const saleRatePerSqft = parseFloat(formData.saleRatePerSqft) || saleRateFlats;
  
  // Saleable Area Split % for Commercial/Mixed projects
  const projectType = formData.projectType || "residential";
  const saleableAreaSplitOffices = parseFloat(formData.saleableAreaSplitOffices) || 0;
  const saleableAreaSplitShops = parseFloat(formData.saleableAreaSplitShops) || 0;
  const commercialSplitTotal = saleableAreaSplitOffices + saleableAreaSplitShops;
  
  // For Mixed projects, get the residential/commercial percentage
  const residentialPercentage = parseFloat(formData.residentialPercentage) || 0;
  const commercialPercentage = parseFloat(formData.commercialPercentage) || 0;
  
  // Calculate separate carpet areas, saleable areas (with loading), and revenues
  let carpetAreaFlats = 0;
  let carpetAreaOffices = 0;
  let carpetAreaShops = 0;
  let saleableAreaFlats = 0;
  let saleableAreaOffices = 0;
  let saleableAreaShops = 0;
  let revenueFlats = 0;
  let revenueOffices = 0;
  let revenueShops = 0;
  
  // Determine the split based on project type
  if (projectType === "commercial" && commercialSplitTotal === 100) {
    // Commercial: 100% goes to Offices and Shops based on user-defined split
    // Calculate carpet area for each type
    carpetAreaOffices = netCarpetForSaleSqft * (saleableAreaSplitOffices / 100);
    carpetAreaShops = netCarpetForSaleSqft * (saleableAreaSplitShops / 100);
    
    // Apply respective loading to get saleable area
    saleableAreaOffices = carpetAreaOffices * (1 + loadingPctOffices / 100);
    saleableAreaShops = carpetAreaShops * (1 + loadingPctShops / 100);
    
    revenueOffices = saleableAreaOffices * saleRateOffices;
    revenueShops = saleableAreaShops * saleRateShops;
  } else if (projectType === "mixed" && residentialPercentage > 0) {
    // Mixed: Flats get residentialPercentage; remaining commercialPercentage is split between Offices & Shops.
    // Sub-split priority:
    //   1) User-provided explicit % (Offices + Shops === 100)
    //   2) Fall back to existing Offices : Shops proportion (works when user only has one commercial type)
    //   3) Default to 100% shops if we have no hint at all
    carpetAreaFlats = netCarpetForSaleSqft * (residentialPercentage / 100);
    const commercialCarpet = netCarpetForSaleSqft * (commercialPercentage / 100);

    let officesRatio = 0;
    let shopsRatio = 0;
    if (Math.abs(commercialSplitTotal - 100) < 0.01) {
      officesRatio = saleableAreaSplitOffices / 100;
      shopsRatio = saleableAreaSplitShops / 100;
    } else if ((existingOffices + existingShops) > 0) {
      // e.g. user has residential + shops only → shopsRatio becomes 1 automatically
      const commercialExisting = existingOffices + existingShops;
      officesRatio = existingOffices / commercialExisting;
      shopsRatio = existingShops / commercialExisting;
    } else {
      // No commercial existing and no explicit sub-split → default 100% shops
      shopsRatio = 1;
    }

    carpetAreaOffices = commercialCarpet * officesRatio;
    carpetAreaShops = commercialCarpet * shopsRatio;

    // Apply respective loading to get saleable area
    saleableAreaFlats = carpetAreaFlats * (1 + loadingPctFlats / 100);
    saleableAreaOffices = carpetAreaOffices * (1 + loadingPctOffices / 100);
    saleableAreaShops = carpetAreaShops * (1 + loadingPctShops / 100);

    revenueFlats = saleableAreaFlats * saleRateFlats;
    revenueOffices = saleableAreaOffices * saleRateOffices;
    revenueShops = saleableAreaShops * saleRateShops;
  } else {
    // Fallback: Use existing area proportions with respective loading
    const totalExistingArea = existingFlats + existingOffices + existingShops;
    if (totalExistingArea > 0) {
      // Calculate carpet area for each type based on existing proportions
      carpetAreaFlats = netCarpetForSaleSqft * (existingFlats / totalExistingArea);
      carpetAreaOffices = netCarpetForSaleSqft * (existingOffices / totalExistingArea);
      carpetAreaShops = netCarpetForSaleSqft * (existingShops / totalExistingArea);
      
      // Apply respective loading to get saleable area
      saleableAreaFlats = carpetAreaFlats * (1 + loadingPctFlats / 100);
      saleableAreaOffices = carpetAreaOffices * (1 + loadingPctOffices / 100);
      saleableAreaShops = carpetAreaShops * (1 + loadingPctShops / 100);
      
      revenueFlats = saleableAreaFlats * saleRateFlats;
      revenueOffices = saleableAreaOffices * saleRateOffices;
      revenueShops = saleableAreaShops * saleRateShops;
    } else {
      // No existing area breakdown - assign all to flats (residential default)
      carpetAreaFlats = netCarpetForSaleSqft;
      saleableAreaFlats = carpetAreaFlats * (1 + loadingPctFlats / 100);
      revenueFlats = saleableAreaFlats * saleRateFlats;
    }
  }
  
  // Total saleable area (with different loading applied to each type)
  const finalSaleAreaSqft = saleableAreaFlats + saleableAreaOffices + saleableAreaShops;
  const finalSaleArea = finalSaleAreaSqft * conversionFactor;
  
  const totalSaleRevenue = revenueFlats + revenueOffices + revenueShops;
  
  // Calculate weighted average sale rate (for backward compatibility)
  const totalExistingArea = existingFlats + existingOffices + existingShops;
  let weightedSaleRate = saleRateFlats;
  if (finalSaleAreaSqft > 0) {
    weightedSaleRate = totalSaleRevenue / finalSaleAreaSqft;
  } else if (totalExistingArea > 0) {
    let num = 0, den = 0;
    if (existingFlats > 0) { num += existingFlats * saleRateFlats; den += existingFlats; }
    if (existingOffices > 0) { num += existingOffices * saleRateOffices; den += existingOffices; }
    if (existingShops > 0) { num += existingShops * saleRateShops; den += existingShops; }
    if (den > 0) weightedSaleRate = num / den;
  }

  // JV (Joint Venture) Calculations — Greenfield mode
  const isGreenfield = formData.projectMode === "greenfield";
  const jvType = formData.jvType || "area_sharing";
  const jvRatio = parseFloat(formData.jvRatioPercent) || 30;
  
  // Separate JV ratios for each property type (always use separate if provided)
  const jvRatioFlats = parseFloat(formData.jvRatioFlats) || jvRatio;
  const jvRatioOffices = parseFloat(formData.jvRatioOffices) || jvRatio;
  const jvRatioShops = parseFloat(formData.jvRatioShops) || jvRatio;
  
  // Security Deposits for Greenfield JV (both refundable and non-refundable)
  const jvSecurityDepositRefundable = isGreenfield ? (parseFloat(formData.jvSecurityDepositRefundable) || 0) : 0;
  const jvSecurityDepositNonRefundable = isGreenfield ? (parseFloat(formData.jvSecurityDepositNonRefundable) || 0) : 0;
  // Total security deposit for initial investment (both types)
  const jvSecurityDeposit = jvSecurityDepositRefundable + jvSecurityDepositNonRefundable;
  // For backward compatibility
  const jvSecurityDepositType = jvSecurityDepositNonRefundable > 0 ? "non-refundable" : "refundable";
  // Refundable deposit: interest at 6% p.a. for entire period is the cost
  const jvRefundableDepositInterest = jvSecurityDepositRefundable * 0.06 * (parseInt(formData.projectDuration) || 0) / 12;
  
  let jvPartnerArea = 0;       // sq mtr (converted from sqft for carpet calcs)
  let jvPartnerAreaSqft = 0;
  let jvPartnerRevenue = 0;
  let jvGSTOnPartnerArea = 0;
  let developerSaleAreaSqft = finalSaleAreaSqft;
  let totalRevenue = totalSaleRevenue;
  
  // Detailed JV breakdown by property type
  let jvPartnerAreaFlats = 0, jvPartnerAreaOffices = 0, jvPartnerAreaShops = 0;
  let jvPartnerRevenueFlats = 0, jvPartnerRevenueOffices = 0, jvPartnerRevenueShops = 0;

  if (isGreenfield && (jvRatioFlats > 0 || jvRatioOffices > 0 || jvRatioShops > 0)) {
    // saleableAreaFlats/Offices/Shops are in SQFT (same as finalSaleAreaSqft)
    
    if (jvType === "area_sharing") {
      // Partner gets jvRatio% of saleable area - different % for each type (in sqft)
      jvPartnerAreaFlats = saleableAreaFlats * (jvRatioFlats / 100);
      jvPartnerAreaOffices = saleableAreaOffices * (jvRatioOffices / 100);
      jvPartnerAreaShops = saleableAreaShops * (jvRatioShops / 100);
      
      const jvPartnerTotalSqft = jvPartnerAreaFlats + jvPartnerAreaOffices + jvPartnerAreaShops;
      jvPartnerArea = jvPartnerTotalSqft * conversionFactor;  // Convert sqft to sq mtr for carpet calculations
      jvPartnerAreaSqft = jvPartnerTotalSqft;  // Already in sqft
      developerSaleAreaSqft = finalSaleAreaSqft - jvPartnerAreaSqft;
      
      // Calculate developer's revenue: partner value = area (sqft) × rate (₹/sqft)
      const partnerFlatsValue = jvPartnerAreaFlats * saleRateFlats;
      const partnerOfficesValue = jvPartnerAreaOffices * saleRateOffices;
      const partnerShopsValue = jvPartnerAreaShops * saleRateShops;
      totalRevenue = totalSaleRevenue - partnerFlatsValue - partnerOfficesValue - partnerShopsValue;
      
      // GST on partner's area (5% of market value for under-construction)
      jvGSTOnPartnerArea = (partnerFlatsValue + partnerOfficesValue + partnerShopsValue) * 0.05;
    } else {
      // Revenue sharing: partner gets jvRatio% of SALE revenue - different % for each type
      // Partner revenue calculated from actual sale rates (not RR rates)
      jvPartnerRevenueFlats = revenueFlats * (jvRatioFlats / 100);
      jvPartnerRevenueOffices = revenueOffices * (jvRatioOffices / 100);
      jvPartnerRevenueShops = revenueShops * (jvRatioShops / 100);
      
      jvPartnerRevenue = jvPartnerRevenueFlats + jvPartnerRevenueOffices + jvPartnerRevenueShops;
      developerSaleAreaSqft = finalSaleAreaSqft; // developer keeps all area
      totalRevenue = totalSaleRevenue - jvPartnerRevenue;
    }
  }

  // FSI & Sanction Costs (FSISanctionCosts.js computes the full breakdown incl. universal addons)
  const fsiTotalCosts = (fsiCosts?.totalCosts || 0);
  // Basic-FSI-only equivalent (informational — never affects P&L). Undefined for MHADA / standalone path.
  const fsiTotalCostsBasicMode = fsiCosts?.totalCostsBasicMode ?? null;
  const amenityTDRCost = fsiCosts?.amenityTDRCost || 0;
  const slumTDRCost = fsiCosts?.slumTDRCost || 0;
  const paidFSICost = fsiCosts?.paidFSICost || 0;
  const ancillaryFSICost = fsiCosts?.ancillaryFSICost || 0;
  const landDevCharges = fsiCosts?.landDevCharges || 0;
  const buildingDevCharges = fsiCosts?.buildingDevCharges || 0;
  const upkar = fsiCosts?.upkar || 0;
  const fireFightingCharges = fsiCosts?.fireFightingCharges || 0;

  // DA Charges
  const totalStampDuty = daChargesCosts?.totalStampDuty || 0;
  const totalRegistration = daChargesCosts?.totalRegistration || 0;
  const scrutinyCharges = daChargesCosts?.scrutinyCharges || 0;
  const lbtCharges = daChargesCosts?.lbtCharges || 0;
  const totalDACharges = daChargesCosts?.totalDACharges || 0;

  // Society Expenses
  const shiftingCharges = societyCosts?.shiftingCharges || 0;
  const corpusCharges = societyCosts?.corpusCharges || 0;
  const bettermentCharges = societyCosts?.bettermentCharges || 0;
  const totalRentalCost = societyCosts?.totalRentalCost || 0;
  const depositCost = societyCosts?.depositCost || 0;
  const brokerageCostSociety = societyCosts?.brokerageCost || 0;
  const pmcCost = societyCosts?.pmcCost || 0;
  const bgCost = societyCosts?.bgCost || 0;
  const totalGST = societyCosts?.totalGST || 0;
  // Society PMC is NOT part of initial expenses
  const totalSocietyExpenses = (societyCosts?.totalSocietyExpenses || 0) - pmcCost;

  // For backward compatibility - use flats loading for general calculations
  const loadingPct = loadingPctFlats;

  // Construction Cost
  const totalConstructedArea = totalCarpetArea + (totalCarpetArea * loadingPct / 100);
  const totalConstructedAreaSqft = totalConstructedArea / conversionFactor;
  const constructionCostPerSqft = parseFloat(formData.constructionCostPerSqft) || 0;
  const baseConstructionCost = totalConstructedAreaSqft * constructionCostPerSqft;
  
  // Additional construction costs (separate heads)
  const recreationalFloorCost = formData.hasRecreationalFloor === "yes"
    ? (parseFloat(formData.recreationalFloorAreaSqft) || 0) * constructionCostPerSqft : 0;
  const basementCost = formData.hasBasements === "yes"
    ? (parseFloat(formData.numBasements) || 0) * (parseFloat(formData.basementAreaPerFloorSqft) || 0) * (parseFloat(formData.basementCostPerSqft) || 0) : 0;
  const additionalParkingFloorCost = formData.hasAdditionalParkingFloors === "yes"
    ? (parseFloat(formData.numAdditionalParkingFloors) || 0) * (parseFloat(formData.additionalParkingFloorAreaSqft) || 0) * (parseFloat(formData.additionalParkingCostPerSqft) || 0) : 0;
  
  // Total construction cost (includes Feb 2026 C2 zone additional construction if applicable)
  const totalConstructionCost = baseConstructionCost + recreationalFloorCost + basementCost + additionalParkingFloorCost + c2AddlConstructionCost;

  // Other Costs - MARKETING EXPENSES
  // Area Sharing: marketing on developer's share area only
  // Revenue Sharing: marketing on entire area (developer sells all)
  let totalMarketingCost = 0;
  const marketingExpensePercent = parseFloat(formData.marketingExpensePercent) || 3;
  const marketingExpenseMode = formData.marketingExpenseMode || "percent";
  const marketingAreaSqft = (isGreenfield && jvType === "area_sharing") ? developerSaleAreaSqft : finalSaleAreaSqft;
  
  if (marketingExpenseMode === "per_sft") {
    // Per sqft rate
    const marketingCostPerSqft = parseFloat(formData.marketingCostPerSqft) || 0;
    totalMarketingCost = marketingAreaSqft * marketingCostPerSqft;
  } else {
    // Percentage mode (default) - % of (Sale Area × Sale Rate)
    totalMarketingCost = (marketingAreaSqft * weightedSaleRate) * (marketingExpensePercent / 100);
  }
  
  const miscExpPerSqft = parseFloat(formData.miscExpPerSqft) || 0;
  const totalMiscExp = finalSaleAreaSqft * miscExpPerSqft;
  
  let totalBrokerageCommission = 0;
  if (formData.brokerageCommissionType === "fixed") {
    totalBrokerageCommission = parseFloat(formData.brokerageCommissionFixed) || 0;
  } else {
    const brokeragePerSqft = parseFloat(formData.brokerageCommissionPerSqft) || 0;
    totalBrokerageCommission = finalSaleAreaSqft * brokeragePerSqft;
  }
  const totalOtherCosts = totalMarketingCost + totalMiscExp + totalBrokerageCommission;

  // Bank Interest - NEW FORMULA
  const duration = parseInt(formData.projectDuration) || 0;
  const durationYears = duration / 12;
  const projectInterestRate = formData.projectInterestRate !== undefined && formData.projectInterestRate !== "" 
    ? parseFloat(formData.projectInterestRate) 
    : 12;

  // Interest Part A: On Ancillary + Paid FSI
  // Amount = (Paid FSI + Ancillary FSI) × 0.80
  // Constraint: This amount must be minimum ₹50 Lakhs less than total cost
  const ancillaryPaidCost = ancillaryFSICost + paidFSICost;
  let interestPartABase = ancillaryPaidCost * 0.80;
  
  // Calculate total costs first to check constraint
  const preliminaryTotalCost = fsiTotalCosts + totalDACharges + totalSocietyExpenses + 
                                totalConstructionCost + totalOtherCosts;
  const maxInterestPartABase = Math.max(0, preliminaryTotalCost - 5000000);
  interestPartABase = Math.min(interestPartABase, maxInterestPartABase);
  
  // Interest Part A @ 8% p.a.
  const interestPartA = interestPartABase * 0.08 * durationYears;

  // Interest Part B: On Project Costs (minus Part A base to avoid double counting)
  // Base = FSI Cost + Sanction Cost + 15% of Construction Cost + Development Cost - Part A Base
  const developmentChargesDA = daChargesCosts?.developmentCharges || 0;
  let baseCostsForInterest = fsiTotalCosts + (totalConstructionCost * 0.15) + developmentChargesDA - interestPartABase;
  baseCostsForInterest = Math.max(0, baseCostsForInterest);
  const interestPartB = baseCostsForInterest * (projectInterestRate / 100) * durationYears;
  
  const totalBankInterest = interestPartA + interestPartB;

  // Initial Investment Calculation (comprehensive - single source of truth)
  // BG: Full amount goes in initial investment; BG cost (Amount × Duration × Rate%) goes in expenses
  const bgRatePA = parseFloat(formData.bgRatePA) || 8;
  const bgBaseAmount = formData.bankGuaranteeRequired === "yes" || formData.bankGuaranteeRequired === true
    ? (parseFloat(formData.bankGuaranteeAmount) || 0)
    : 0;
  
  // BG Cost calculation: flat vs staged release
  let bankGuaranteeCost = 0;
  let bgStages = [];
  const bgStagedRelease = formData.bgStagedRelease === "yes";
  const durationMonths = parseInt(formData.projectDuration) || 0;
  
  if (bgBaseAmount > 0) {
    const totalBGMonths = durationMonths + 3; // project duration + 3 months buffer
    
    if (bgStagedRelease && totalBGMonths > 0) {
      // Staged release: divide into parts nearest to 1 year
      const numStages = Math.ceil(totalBGMonths / 12);
      const stageMonths = Math.round(totalBGMonths / numStages);
      const perStage = bgBaseAmount / numStages;
      
      for (let i = 0; i < numStages; i++) {
        const outstanding = bgBaseAmount - (perStage * i);
        const cost = outstanding * (bgRatePA / 100) * (stageMonths / 12);
        bankGuaranteeCost += cost;
        bgStages.push({ stage: i + 1, outstanding, months: stageMonths, cost, releasedAt: stageMonths * (i + 1) });
      }
    } else {
      // Flat: full amount for entire duration
      bankGuaranteeCost = bgBaseAmount * (bgRatePA / 100) * ((durationMonths + 3) / 12);
    }
  }
  
  const bettermentPaymentTiming = formData.bettermentPaymentTiming || "at_possession";
  const bettermentOnVacation = bettermentPaymentTiming === "split" ? bettermentCharges * 0.5 : 0;
  
  // Comprehensive Initial Investment (same formula used everywhere)
  const tdrCost = amenityTDRCost + slumTDRCost;
  const paidFSIAncillaryFull = paidFSICost + ancillaryFSICost;
  const minPaidFSIInvestment = Math.max(paidFSIAncillaryFull * 0.20, 5000000);
  const ulcCharges = fsiCosts?.ulcCharges || 0;
  const sanctionCostsExTDRPaid = fsiTotalCosts - tdrCost - paidFSIAncillaryFull - ulcCharges;
  const shiftingInInitial = shiftingCharges * 0.5;
  
  // PMC is NOT part of initial investment
  // Security Deposit is ALWAYS part of initial investment (even if refundable - it's still an outflow initially)
  const initialInvestment = tdrCost + minPaidFSIInvestment + sanctionCostsExTDRPaid + 
                            totalDACharges + shiftingInInitial + ulcCharges + bgBaseAmount +
                            bettermentOnVacation + depositCost + brokerageCostSociety + jvSecurityDeposit;

  // Initial Investment under BASIC sanction (Basic FSI 1.1 + full ancillary — no Paid FSI + no TDR).
  // Uses the pre-computed basic-mode FSI cost total from FSISanctionCosts.js if available.
  // Derivation: strip out TDR cost + minPaidFSI cash portion; replace fsi sanction ex-TDR-Paid with the basic scenario's remainder.
  let initialInvestmentBasicMode = null;
  if (fsiCosts?.totalCostsBasicMode != null && fsiCosts.totalCostsBasicMode > 0) {
    // Basic-mode FSI cost has no TDR + no paid FSI; only sanction/ancillary etc.
    const basicSanctionCostsExTDRPaid = fsiCosts.totalCostsBasicMode - ulcCharges;
    initialInvestmentBasicMode = 0
      + basicSanctionCostsExTDRPaid
      + totalDACharges
      + shiftingInInitial
      + ulcCharges
      + bgBaseAmount
      + bettermentOnVacation
      + depositCost
      + brokerageCostSociety
      + jvSecurityDeposit;
  }

  // Totals
  // Non-refundable deposit is an expense (added to landowner's share, won't come back)
  // Refundable deposit: interest cost (6% p.a.) is the expense, not the deposit itself
  const totalExpenses = fsiTotalCosts + totalDACharges + totalSocietyExpenses + 
                        totalConstructionCost + totalOtherCosts + totalBankInterest + pmcCost + jvGSTOnPartnerArea + jvSecurityDepositNonRefundable + jvRefundableDepositInterest;
  const grossProfit = totalRevenue - totalExpenses;
  const profitMarginPercent = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
  const returnOnInvestment = totalExpenses > 0 ? (grossProfit / totalExpenses) * 100 : 0;

  // Area sharing calculations
  const totalSocietyCarpet = existingCarpetArea + totalFreeArea;
  const developerCarpet = isGreenfield && jvType === "area_sharing" 
    ? balanceCarpetArea - jvPartnerArea 
    : balanceCarpetArea;
  const jvPartnerCarpet = isGreenfield ? jvPartnerArea : 0;
  // For greenfield JV, compute effective share % from the actual per-type weighted split
  // (jvPartnerAreaSqft / jvPartnerRevenue are already sized using jvRatioFlats/Offices/Shops).
  // Fall back to formData.jvRatioPercent only if we have no computed partner amount yet.
  let greenfieldEffectiveJvPct = jvRatio;
  if (isGreenfield) {
    if (jvType === "area_sharing" && finalSaleAreaSqft > 0) {
      greenfieldEffectiveJvPct = (jvPartnerAreaSqft / finalSaleAreaSqft) * 100;
    } else if (jvType !== "area_sharing" && totalSaleRevenue > 0) {
      greenfieldEffectiveJvPct = (jvPartnerRevenue / totalSaleRevenue) * 100;
    }
  }
  const societySharePercent = isGreenfield 
    ? greenfieldEffectiveJvPct 
    : (totalCarpetArea > 0 ? (totalSocietyCarpet / totalCarpetArea) * 100 : 0);
  const developerSharePercent = isGreenfield 
    ? (100 - greenfieldEffectiveJvPct) 
    : (totalCarpetArea > 0 ? (developerCarpet / totalCarpetArea) * 100 : 0);

  // Parking compensation details
  const existingParkings = parseFloat(formData.existingParkings) || 0;
  const parkingCompensationType = formData.parkingCompensationType || "none";
  const parkingCompensationPayout = parkingCompensationType === "payout" 
    ? (parseFloat(formData.compensationPayout) || 0) 
    : 0;

  return {
    // Revenue
    totalSaleRevenue,
    totalRevenue,
    finalSaleAreaSqft,
    saleRatePerSqft: weightedSaleRate,
    saleRateFlats,
    saleRateOffices,
    saleRateShops,
    // Loading percentages by property type
    loadingPctFlats,
    loadingPctOffices,
    loadingPctShops,
    // Carpet area breakdown (before loading)
    carpetAreaFlats,
    carpetAreaOffices,
    carpetAreaShops,
    // Revenue breakdown by property type
    saleableAreaFlats,
    saleableAreaOffices,
    saleableAreaShops,
    revenueFlats,
    revenueOffices,
    revenueShops,
    // FSI Costs
    amenityTDRCost,
    slumTDRCost,
    paidFSICost,
    ancillaryFSICost,
    landDevCharges,
    buildingDevCharges,
    upkar,
    fireFightingCharges,
    fsiTotalCosts,
    // DA Charges
    totalStampDuty,
    totalRegistration,
    scrutinyCharges,
    lbtCharges,
    totalDACharges,
    // Society Expenses
    shiftingCharges,
    corpusCharges,
    bettermentCharges,
    totalRentalCost,
    depositCost,
    brokerageCostSociety,
    pmcCost,
    bgCost,
    totalGST,
    totalSocietyExpenses,
    // Construction
    totalConstructedAreaSqft,
    constructionCostPerSqft,
    totalConstructionCost,
    baseConstructionCost,
    recreationalFloorCost,
    basementCost,
    additionalParkingFloorCost,
    // Other Costs
    totalMarketingCost,
    marketingExpensePercent,
    totalMiscExp,
    totalBrokerageCommission,
    totalOtherCosts,
    // Bank Interest
    interestPartA,
    interestPartB,
    totalBankInterest,
    // Initial Investment
    initialInvestment,
    bankGuaranteeCost,
    bgBaseAmount,
    bgRatePA,
    bgStagedRelease,
    bgStages,
    bettermentOnVacation,
    tdrCost,
    paidFSIAncillaryFull,
    minPaidFSIInvestment,
    ulcCharges,
    sanctionCostsExTDRPaid,
    shiftingInInitial,
    // Totals
    totalExpenses,
    grossProfit,
    profitMarginPercent,
    returnOnInvestment,
    // Area
    totalFSIArea,
    totalCarpetArea,
    existingCarpetArea,
    totalFreeArea,
    balanceCarpetArea,
    societySharePercent,
    developerSharePercent,
    durationYears,
    // Basic-FSI-only cost benchmark for Dashboard what-if insight
    fsiTotalCostsBasicMode,
    initialInvestmentBasicMode,
    // Feb 2026 C2 zone additional construction (informational display)
    c2AddlConstructionCost,
    c2AddlAreaSqft,
    isC2Zone: _isC2,
    // Free area breakdown
    additionalFreeCarpetFlats,
    additionalFreeCarpetOffices,
    additionalFreeCarpetShops,
    freeAreaPercentFlats,
    freeAreaPercentOffices,
    freeAreaPercentShops,
    // Terrace/Garden with increment
    convertedTerrace,
    convertedGarden,
    convertedTerraceGarden,
    incrementOnTerrace,
    incrementOnGarden,
    totalTerraceGardenWithIncrement,
    // Parking Compensation
    existingParkings,
    parkingCompensationType,
    parkingCompensationCarpet,
    parkingCompensationPayout,
    // JV (Greenfield)
    isGreenfield,
    jvType,
    jvRatio,
    jvRatioFlats,
    jvRatioOffices,
    jvRatioShops,
    jvPartnerArea,
    jvPartnerAreaSqft,
    jvPartnerAreaFlats,
    jvPartnerAreaOffices,
    jvPartnerAreaShops,
    jvPartnerRevenue,
    jvPartnerRevenueFlats,
    jvPartnerRevenueOffices,
    jvPartnerRevenueShops,
    jvGSTOnPartnerArea,
    developerSaleAreaSqft,
    jvPartnerCarpet,
    // Security Deposit (Greenfield JV)
    jvSecurityDeposit,
    jvSecurityDepositRefundable,
    jvSecurityDepositNonRefundable,
    jvSecurityDepositType,
    jvRefundableDepositInterest
  };
};

export const formatCrores = (value) => {
  if (!value || isNaN(value)) return '₹0.00 Cr';
  const crores = value / 10000000;
  return `₹${crores.toFixed(2)} Cr`;
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(value);
};


/**
 * Calculate both JV scenarios (Area Sharing vs Revenue Sharing) for side-by-side P&L comparison
 * Key differences:
 * - Area Sharing: Marketing cost on developer's share area only, GST on landowner area
 * - Revenue Sharing: Marketing cost on entire area, No GST
 */
export const calculateDualJVComparison = (formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts) => {
  // Get the base metrics with the currently selected JV type
  const baseMetrics = calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
  if (!baseMetrics || !baseMetrics.isGreenfield) return null;

  const {
    totalSaleRevenue, finalSaleAreaSqft, saleRatePerSqft: weightedSaleRate,
    saleableAreaFlats, saleableAreaOffices, saleableAreaShops,
    revenueFlats, revenueOffices, revenueShops,
    saleRateFlats, saleRateOffices, saleRateShops,
    jvRatioFlats, jvRatioOffices, jvRatioShops,
    fsiTotalCosts, totalDACharges, totalSocietyExpenses,
    totalConstructionCost, totalBankInterest, pmcCost,
    totalMiscExp, totalBrokerageCommission,
    marketingExpensePercent, jvSecurityDepositNonRefundable,
    jvRefundableDepositInterest, durationYears
  } = baseMetrics;

  const marketingExpenseMode = formData.marketingExpenseMode || "percent";
  const marketingCostPerSqft = parseFloat(formData.marketingCostPerSqft) || 0;

  // ============ AREA SHARING SCENARIO ============
  const as_partnerFlats = saleableAreaFlats * (jvRatioFlats / 100);
  const as_partnerOffices = saleableAreaOffices * (jvRatioOffices / 100);
  const as_partnerShops = saleableAreaShops * (jvRatioShops / 100);
  const as_partnerTotalSqft = as_partnerFlats + as_partnerOffices + as_partnerShops;
  const as_developerSaleArea = finalSaleAreaSqft - as_partnerTotalSqft;
  
  const as_partnerValue = as_partnerFlats * saleRateFlats + as_partnerOffices * saleRateOffices + as_partnerShops * saleRateShops;
  const as_totalRevenue = totalSaleRevenue - as_partnerValue;
  const as_gst = as_partnerValue * 0.05;  // GST applicable on area given to landowner

  // Marketing on DEVELOPER'S share area only
  let as_marketingCost;
  if (marketingExpenseMode === "per_sft") {
    as_marketingCost = as_developerSaleArea * marketingCostPerSqft;
  } else {
    as_marketingCost = (as_developerSaleArea * weightedSaleRate) * (marketingExpensePercent / 100);
  }
  const as_otherCosts = as_marketingCost + totalMiscExp + totalBrokerageCommission;
  const as_totalExpenses = fsiTotalCosts + totalDACharges + totalSocietyExpenses +
    totalConstructionCost + as_otherCosts + totalBankInterest + pmcCost + as_gst + jvSecurityDepositNonRefundable + jvRefundableDepositInterest;
  const as_grossProfit = as_totalRevenue - as_totalExpenses;
  const as_profitMargin = as_totalRevenue > 0 ? (as_grossProfit / as_totalRevenue) * 100 : 0;

  // ============ REVENUE SHARING SCENARIO ============
  const rs_partnerFlats = revenueFlats * (jvRatioFlats / 100);
  const rs_partnerOffices = revenueOffices * (jvRatioOffices / 100);
  const rs_partnerShops = revenueShops * (jvRatioShops / 100);
  const rs_partnerRevenue = rs_partnerFlats + rs_partnerOffices + rs_partnerShops;
  const rs_totalRevenue = totalSaleRevenue - rs_partnerRevenue;
  // No GST in revenue sharing

  // Marketing on ENTIRE area (developer sells all)
  let rs_marketingCost;
  if (marketingExpenseMode === "per_sft") {
    rs_marketingCost = finalSaleAreaSqft * marketingCostPerSqft;
  } else {
    rs_marketingCost = (finalSaleAreaSqft * weightedSaleRate) * (marketingExpensePercent / 100);
  }
  const rs_otherCosts = rs_marketingCost + totalMiscExp + totalBrokerageCommission;
  const rs_totalExpenses = fsiTotalCosts + totalDACharges + totalSocietyExpenses +
    totalConstructionCost + rs_otherCosts + totalBankInterest + pmcCost + jvSecurityDepositNonRefundable + jvRefundableDepositInterest;
  const rs_grossProfit = rs_totalRevenue - rs_totalExpenses;
  const rs_profitMargin = rs_totalRevenue > 0 ? (rs_grossProfit / rs_totalRevenue) * 100 : 0;

  return {
    areaSharing: {
      totalSaleRevenue,
      landownerShare: as_partnerValue,
      totalRevenue: as_totalRevenue,
      developerSaleArea: as_developerSaleArea,
      jvPartnerAreaSqft: as_partnerTotalSqft,
      gstOnPartnerArea: as_gst,
      marketingCost: as_marketingCost,
      otherCosts: as_otherCosts,
      totalExpenses: as_totalExpenses,
      grossProfit: as_grossProfit,
      profitMarginPercent: as_profitMargin,
      fsiTotalCosts, totalDACharges, totalSocietyExpenses, totalConstructionCost, totalBankInterest,
      jvSecurityDepositNonRefundable, jvRefundableDepositInterest,
    },
    revenueSharing: {
      totalSaleRevenue,
      landownerShare: rs_partnerRevenue,
      totalRevenue: rs_totalRevenue,
      developerSaleArea: finalSaleAreaSqft,
      jvPartnerRevenue: rs_partnerRevenue,
      gstOnPartnerArea: 0,
      marketingCost: rs_marketingCost,
      otherCosts: rs_otherCosts,
      totalExpenses: rs_totalExpenses,
      grossProfit: rs_grossProfit,
      profitMarginPercent: rs_profitMargin,
      fsiTotalCosts, totalDACharges, totalSocietyExpenses, totalConstructionCost, totalBankInterest,
      jvSecurityDepositNonRefundable, jvRefundableDepositInterest,
    },
    selectedType: formData.jvType || "area_sharing",
    jvRatioFlats, jvRatioOffices, jvRatioShops,
  };
};